import React, { useState } from 'react';
import { Radio, Loader2, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { pingAdapter, getAdapterBanf, createAdapterBanfTestrun, createAdapterBanfReal, deleteAdapterBanf } from '../../core/api/apiService';

const DEFAULT_BANFN = '0010082507';

const AdapterTestPanel = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [banfn, setBanfn] = useState(DEFAULT_BANFN);
  const [createFields, setCreateFields] = useState({
    ekgrp: '002',
    matkl: '200',
    sakto: '0000602000',
    kostl: '0000140100',
    short_text: 'GineeX Adapter Test',
    menge: '1',
    meins: 'ST',
    preis: '100.00',
    peinh: '1'
  });

  const [realFields, setRealFields] = useState({
    ekgrp: '002',
    matkl: '200',
    sakto: '0000602000',
    kostl: '0000140100',
    short_text: 'GineeX Adapter Test',
    menge: '1',
    meins: 'ST',
    preis: '100.00',
    peinh: '1'
  });
  const [realArmed, setRealArmed] = useState(false);

  const [deleteBanfn, setDeleteBanfn] = useState('');
  const [deleteItem, setDeleteItem] = useState('00010');
  const [deleteArmed, setDeleteArmed] = useState(false);

  const [loadingKey, setLoadingKey] = useState(null);
  const [result, setResult] = useState(null);

  const runAction = async (key, action) => {
    setLoadingKey(key);
    setResult(null);
    try {
      const data = await action();
      setResult({ ok: true, data });
      showSuccess?.(t('admin.adapter.success'));
      if (key === 'real' && data?.result?.banfn) {
        setDeleteBanfn(data.result.banfn);
      }
    } catch (error) {
      setResult({ ok: false, error: error.message });
      showError?.(error.message || t('admin.adapter.error'));
    } finally {
      setLoadingKey(null);
    }
  };

  const cardClass = `p-6 rounded-2xl border ${
    isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
  }`;

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDarkMode
      ? 'bg-white/5 border-white/20 text-white placeholder-white/40'
      : 'bg-white border-gray-300 text-gray-900'
  }`;

  const buttonClass = (busy) => `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
    busy
      ? 'bg-gray-400 text-white cursor-not-allowed'
      : 'bg-brand text-white hover:opacity-90'
  }`;

  return (
    <div className="space-y-6">
      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-500'}`}>
        <Radio className="w-4 h-4" />
        {t('admin.adapter.intro')}
      </div>

      {/* Ping */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.adapter.pingTitle')}
        </h3>
        <button
          className={buttonClass(loadingKey === 'ping')}
          disabled={loadingKey !== null}
          onClick={() => runAction('ping', () => pingAdapter(user))}
        >
          {loadingKey === 'ping' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('admin.adapter.pingButton')}
        </button>
      </div>

      {/* Read BANF */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.adapter.readTitle')}
        </h3>
        <div className="flex items-center gap-2 mb-3">
          <input
            className={inputClass}
            value={banfn}
            onChange={(e) => setBanfn(e.target.value)}
            placeholder="BANFN"
          />
        </div>
        <button
          className={buttonClass(loadingKey === 'read')}
          disabled={loadingKey !== null || !banfn}
          onClick={() => runAction('read', () => getAdapterBanf(user, banfn))}
        >
          {loadingKey === 'read' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('admin.adapter.readButton')}
        </button>
      </div>

      {/* Create BANF (dry-run) */}
      <div className={cardClass}>
        <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('admin.adapter.createTitle')}
        </h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
          {t('admin.adapter.createHint')}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {Object.keys(createFields).map((key) => (
            <div key={key}>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{key}</label>
              <input
                className={inputClass}
                value={createFields[key]}
                onChange={(e) => setCreateFields({ ...createFields, [key]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <button
          className={buttonClass(loadingKey === 'create')}
          disabled={loadingKey !== null}
          onClick={() => runAction('create', () => createAdapterBanfTestrun(user, createFields))}
        >
          {loadingKey === 'create' && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('admin.adapter.createButton')}
        </button>
      </div>

      {/* Create BANF (REAL — no undo) */}
      <div className={`p-6 rounded-2xl border-2 ${isDarkMode ? 'bg-red-950/20 border-red-500/40' : 'bg-red-50 border-red-300'}`}>
        <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
          <AlertTriangle className="w-4 h-4" />
          {t('admin.adapter.realTitle')}
        </h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-red-300/70' : 'text-red-600'}`}>
          {t('admin.adapter.realHint')}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {Object.keys(realFields).map((key) => (
            <div key={key}>
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{key}</label>
              <input
                className={inputClass}
                value={realFields[key]}
                onChange={(e) => { setRealFields({ ...realFields, [key]: e.target.value }); setRealArmed(false); }}
              />
            </div>
          ))}
        </div>
        {!realArmed ? (
          <button
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
            disabled={loadingKey !== null}
            onClick={() => setRealArmed(true)}
          >
            {t('admin.adapter.realButton')}
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
              {t('admin.adapter.realArmedWarning')}
            </span>
            <button
              className={buttonClass(loadingKey === 'real')}
              style={{ backgroundColor: '#dc2626' }}
              disabled={loadingKey !== null}
              onClick={() => {
                setRealArmed(false);
                runAction('real', () => createAdapterBanfReal(user, realFields));
              }}
            >
              {loadingKey === 'real' && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('admin.adapter.realConfirmButton')}
            </button>
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-400 text-gray-500 hover:bg-gray-100/10"
              disabled={loadingKey !== null}
              onClick={() => setRealArmed(false)}
            >
              {t('admin.adapter.realCancelButton')}
            </button>
          </div>
        )}
      </div>

      {/* Delete/undo BANF (reversible soft-flag) */}
      <div className={`p-6 rounded-2xl border-2 ${isDarkMode ? 'bg-amber-950/20 border-amber-500/40' : 'bg-amber-50 border-amber-300'}`}>
        <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          <AlertTriangle className="w-4 h-4" />
          {t('admin.adapter.deleteTitle')}
        </h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
          {t('admin.adapter.deleteHint')}
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3 max-w-md">
          <div>
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>banfn</label>
            <input
              className={inputClass}
              value={deleteBanfn}
              onChange={(e) => { setDeleteBanfn(e.target.value); setDeleteArmed(false); }}
              placeholder="BANFN"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>item</label>
            <input
              className={inputClass}
              value={deleteItem}
              onChange={(e) => { setDeleteItem(e.target.value); setDeleteArmed(false); }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            className={buttonClass(loadingKey === 'delete-dry')}
            disabled={loadingKey !== null || !deleteBanfn}
            onClick={() => runAction('delete-dry', () => deleteAdapterBanf(user, { banfn: deleteBanfn, item: deleteItem, testrun: true }))}
          >
            {loadingKey === 'delete-dry' && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('admin.adapter.deleteDryRunButton')}
          </button>
          {!deleteArmed ? (
            <button
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              disabled={loadingKey !== null || !deleteBanfn}
              onClick={() => setDeleteArmed(true)}
            >
              {t('admin.adapter.deleteButton')}
            </button>
          ) : (
            <>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                {t('admin.adapter.deleteArmedWarning')}
              </span>
              <button
                className={buttonClass(loadingKey === 'delete')}
                style={{ backgroundColor: '#d97706' }}
                disabled={loadingKey !== null}
                onClick={() => {
                  setDeleteArmed(false);
                  runAction('delete', () => deleteAdapterBanf(user, { banfn: deleteBanfn, item: deleteItem }));
                }}
              >
                {loadingKey === 'delete' && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('admin.adapter.deleteConfirmButton')}
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-400 text-gray-500 hover:bg-gray-100/10"
                disabled={loadingKey !== null}
                onClick={() => setDeleteArmed(false)}
              >
                {t('admin.adapter.deleteCancelButton')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={cardClass}>
          <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('admin.adapter.resultTitle')}
          </h3>
          <pre className={`text-xs overflow-auto p-3 rounded-lg max-h-96 ${
            isDarkMode ? 'bg-black/30 text-green-300' : 'bg-gray-50 text-gray-800'
          }`}>
            {JSON.stringify(result.ok ? result.data : { error: result.error }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default AdapterTestPanel;
